import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import type { BacktestInvoiceRow } from "@/types/backtest";
import { formatCurrency } from "@/lib/currency";
import { exportBacktestSuppliersCsv } from "@/lib/csvExport";

interface SupplierRow {
  supplierName: string;
  eligible: number;
  missed: number;
  captured: number;
  missedSavings: number;
  capturedSavings: number;
  captureRate: number;
}

function buildSupplierData(rows: BacktestInvoiceRow[]): SupplierRow[] {
  const bySupplier: Record<string, SupplierRow> = {};

  for (const row of rows) {
    if (!row.was_eligible) continue;
    const name = row.supplier_name ?? "Unknown";
    if (!bySupplier[name]) {
      bySupplier[name] = {
        supplierName: name,
        eligible: 0,
        missed: 0,
        captured: 0,
        missedSavings: 0,
        capturedSavings: 0,
        captureRate: 0,
      };
    }
    bySupplier[name].eligible++;
    if (row.discount_captured) {
      bySupplier[name].captured++;
      bySupplier[name].capturedSavings += row.captured_savings;
    } else {
      bySupplier[name].missed++;
      bySupplier[name].missedSavings += row.missed_savings;
    }
  }

  return Object.values(bySupplier)
    .map((s) => ({
      ...s,
      captureRate: s.eligible > 0 ? (s.captured / s.eligible) * 100 : 0,
    }))
    .sort((a, b) => b.missedSavings - a.missedSavings);
}

interface Props {
  rows: BacktestInvoiceRow[];
}

export function SupplierBreakdownTable({ rows }: Props) {
  const data = buildSupplierData(rows);

  if (data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">No eligible invoices found.</p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1.5"
          onClick={() => exportBacktestSuppliersCsv(rows)}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </Button>
      </div>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Supplier</TableHead>
              <TableHead className="text-right">Invoices</TableHead>
              <TableHead className="text-right">Missed Savings</TableHead>
              <TableHead className="text-right">Capture Rate</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((s) => (
              <TableRow key={s.supplierName}>
                <TableCell className="font-medium text-sm">{s.supplierName}</TableCell>
                <TableCell className="text-right text-sm text-muted-foreground">
                  {s.eligible}
                </TableCell>
                <TableCell className="text-right text-sm font-medium text-destructive">
                  {s.missedSavings > 0 ? formatCurrency(s.missedSavings) : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <Badge
                    variant="outline"
                    className={
                      s.captureRate >= 80
                        ? "border-green-500 text-green-700"
                        : s.captureRate >= 40
                          ? "border-yellow-500 text-yellow-700"
                          : "border-red-400 text-red-700"
                    }
                  >
                    {s.captureRate.toFixed(0)}%
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
